import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Eye, EyeOff, Lock, AlertTriangle, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";

// Validation schema for the password reset form
const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .max(72, "Password must not exceed 72 characters") // bcrypt limit
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/,
        "Password must include at least one uppercase letter, one lowercase letter, one number, and one special character"
      )
      .refine((password) => !password.includes(" "), {
        message: "Password must not contain spaces",
      }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

// Add password strength calculation
const calculatePasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 8) strength += 20;
  if (password.length >= 12) strength += 20;
  if (/[A-Z]/.test(password)) strength += 20;
  if (/[a-z]/.test(password)) strength += 20;
  if (/[0-9]/.test(password)) strength += 10;
  if (/[^A-Za-z0-9]/.test(password)) strength += 10;
  return Math.min(100, strength);
};

const getStrengthColor = (strength: number): string => {
  if (strength < 40) return "bg-destructive";
  if (strength < 70) return "bg-yellow-500";
  return "bg-green-500";
};

const ResetPasswordPage = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [isInvitation, setIsInvitation] = useState(false);
  const [isTokenLoading, setIsTokenLoading] = useState(true);
  const [userData, setUserData] = useState<{ name?: string } | null>(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Extract the token from the URL and check session
  useEffect(() => {
    const checkAuthAndToken = async () => {
      // Start by indicating we're loading the token
      setIsTokenLoading(true);
      setError(null);

      try {
        // Log all URL parts for debugging
        console.log("Full URL:", window.location.href);
        console.log("URL pathname:", location.pathname);
        console.log("URL search:", location.search);
        console.log("URL hash:", location.hash);

        // Check if we're dealing with a direct Supabase invitation link
        const searchParams = new URLSearchParams(location.search);
        const directToken = searchParams.get("token");
        const inviteType = searchParams.get("type");
        const redirectTo = searchParams.get("redirect_to");

        // Check if we're being redirected from Supabase's verification endpoint
        if (
          redirectTo &&
          (location.pathname.includes("/verify") ||
            location.pathname.includes("/auth/v1/verify"))
        ) {
          console.log("Detected redirect_to parameter:", redirectTo);

          // Create URL to our own reset password page with token
          const resetUrl = new URL(
            `${window.location.origin}/admin/reset-password`
          );
          resetUrl.searchParams.append("token", directToken || "");
          resetUrl.searchParams.append("type", inviteType || "invite");

          console.log(
            "Redirecting to our password reset page:",
            resetUrl.toString()
          );
          window.location.href = resetUrl.toString();
          return;
        }

        // If we have a direct token in the URL (typical Supabase invitation link format)
        if (directToken && inviteType === "invite") {
          console.log("Detected direct invitation token in URL");
          setToken(directToken);
          setIsInvitation(true);

          // Set session using the direct token
          try {
            await supabase.auth.verifyOtp({
              token_hash: directToken,
              type: "invite",
            });
            console.log("Verified invitation token directly");
          } catch (err) {
            console.error("Error verifying direct token:", err);
          }

          // Finish loading early since we have the token
          setIsTokenLoading(false);
          return;
        }

        // Rest of the function to handle other token formats
        // First check if we have an active session
        const { data: sessionData } = await supabase.auth.getSession();

        console.log("Has active session:", !!sessionData?.session);

        // Get token from hash or URL params (handling both formats)
        let hashParams = location.hash;

        // Handle double hash case (#invitation#access_token=...)
        if (hashParams.startsWith("#invitation#")) {
          hashParams = hashParams.replace("#invitation#", "#");
        }

        const params = new URLSearchParams(
          hashParams.substring(1) || location.search
        );

        // For debugging, log all params
        console.log("URL parameters found:");
        for (const [key, value] of params.entries()) {
          console.log(`${key}: ${value}`);
        }

        // Check for token in different possible locations
        const accessToken =
          params.get("access_token") ||
          params.get("token") ||
          sessionData?.session?.access_token;

        const type =
          params.get("type") ||
          (params.get("token") ? "invite" : undefined) ||
          (hashParams.includes("#invitation") ? "invite" : undefined);

        console.log("Token type:", type);
        console.log("Has access token:", !!accessToken);

        // If we already have a valid session or token
        if (accessToken) {
          console.log("Using token:", accessToken.substring(0, 10) + "...");
          setToken(accessToken);

          // Check for invitation-specific data
          const isInvite = type === "invite" || params.has("token");
          setIsInvitation(isInvite);

          // Try to extract user data if available
          try {
            if (sessionData?.session?.user) {
              const user = sessionData.session.user;
              setUserData({
                name: user.user_metadata?.name || user.email,
              });
            }
          } catch (err) {
            console.error("Error extracting user data:", err);
          }

          // If we have a token but no session, set the session
          if (
            !sessionData?.session &&
            (params.get("access_token") || params.get("token"))
          ) {
            console.log("Setting session with token");
            await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: params.get("refresh_token") || "",
            });

            // Try to get user data after setting session
            try {
              const { data } = await supabase.auth.getUser();
              if (data?.user) {
                setUserData({
                  name: data.user.user_metadata?.name || data.user.email,
                });
              }
            } catch (err) {
              console.error("Error fetching user data:", err);
            }
          }
        } else {
          // Only show error if we have no token AND no session
          setError(
            "Invalid or missing password reset token. Please request a new password reset link."
          );
          console.error("No token found in URL or session");
        }
      } catch (err) {
        console.error("Error checking authentication:", err);
        setError(
          "Error verifying your authentication. Please try again or request a new link."
        );
      } finally {
        // Finish loading
        setIsTokenLoading(false);
      }
    };

    checkAuthAndToken();
  }, [location]);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  // Add password strength check
  const handlePasswordChange = (value: string) => {
    const strength = calculatePasswordStrength(value);
    setPasswordStrength(strength);
  };

  const onSubmit = async (values: ResetPasswordFormValues) => {
    if (!token) {
      setError("Missing authentication token. Please request a new link.");
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Add rate limiting check
      const rateLimitKey = `password_reset_${Date.now()}`;
      const previousAttempt = localStorage.getItem(rateLimitKey);
      if (previousAttempt) {
        const timeSinceLastAttempt = Date.now() - parseInt(previousAttempt);
        if (timeSinceLastAttempt < 1000) {
          // 1 second delay between attempts
          throw new Error("Please wait a moment before trying again.");
        }
      }
      localStorage.setItem(rateLimitKey, Date.now().toString());

      // Update the user's password
      const { error: updateError } = await supabase.auth.updateUser({
        password: values.password,
      });

      if (updateError) {
        if (updateError.message.includes("New password should be different")) {
          setError(
            "The new password must be different from your current password. Please choose a different password."
          );
        } else {
          setError(updateError.message);
        }
        return;
      }

      // Clear any stored session data
      localStorage.removeItem(rateLimitKey);

      // Show success toast
      toast({
        title: isInvitation
          ? "Account Setup Complete"
          : "Password Reset Complete",
        description: isInvitation
          ? "Your account has been set up successfully. You can now log in."
          : "Your password has been reset successfully. You can now log in.",
        duration: 5000,
      });

      // Clear form data for security
      form.reset();

      // Add small delay before redirect for better UX
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Redirect to login page
      navigate("/admin/login");
    } catch (error) {
      console.error("Error setting password:", error);
      setError(error.message || "Failed to set password. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Show loading state while checking token
  if (isTokenLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">
            Verifying your link...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            {isInvitation ? "Set Up Your Account" : "Reset Your Password"}
          </CardTitle>
          <CardDescription className="text-center">
            {isInvitation
              ? userData?.name
                ? `Welcome, ${userData.name}! Create a password to complete your account setup.`
                : "Create a password to complete your account setup."
              : "Enter your new password below"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && !token && (
            <Alert variant="destructive" className="mb-6">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!token ? (
            <div className="text-center py-6">
              <div className="inline-flex p-3 mx-auto rounded-full bg-amber-100 mb-4">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-medium mb-2">Invalid Reset Link</h3>
              <p className="text-muted-foreground mb-4">
                The password reset link you clicked appears to be invalid or has
                expired. Please request a new password reset.
              </p>
              <Button onClick={() => navigate("/admin/login")}>
                Return to Login
              </Button>
            </div>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <FormControl>
                        <div className="space-y-2">
                          <div className="relative">
                            <Input
                              placeholder="••••••••"
                              type={showPassword ? "text" : "password"}
                              autoComplete="new-password"
                              disabled={isProcessing}
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                handlePasswordChange(e.target.value);
                              }}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute right-1 top-1/2 -translate-y-1/2"
                              onClick={() => setShowPassword(!showPassword)}
                              disabled={isProcessing}
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                              <span className="sr-only">
                                {showPassword
                                  ? "Hide password"
                                  : "Show password"}
                              </span>
                            </Button>
                          </div>
                          {field.value && (
                            <div className="space-y-2">
                              <Progress
                                value={passwordStrength}
                                className={`h-2 ${getStrengthColor(
                                  passwordStrength
                                )}`}
                              />
                              <p className="text-xs text-muted-foreground">
                                Password strength:{" "}
                                {passwordStrength < 40 && "Weak"}
                                {passwordStrength >= 40 &&
                                  passwordStrength < 70 &&
                                  "Medium"}
                                {passwordStrength >= 70 && "Strong"}
                              </p>
                            </div>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                      <ul className="text-xs text-muted-foreground mt-2 list-disc pl-4 space-y-1">
                        <li>At least 8 characters long</li>
                        <li>Include uppercase and lowercase letters</li>
                        <li>Include at least one number</li>
                        <li>
                          Include at least one special character (!@#$%^&*)
                        </li>
                        <li>No spaces allowed</li>
                      </ul>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="••••••••"
                            type={showConfirmPassword ? "text" : "password"}
                            autoComplete="new-password"
                            disabled={isProcessing}
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1/2 -translate-y-1/2"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            disabled={isProcessing}
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                            <span className="sr-only">
                              {showConfirmPassword
                                ? "Hide password"
                                : "Show password"}
                            </span>
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <div className="flex items-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      <span>
                        {isInvitation ? "Set Password" : "Reset Password"}
                      </span>
                    </div>
                  )}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-center text-sm text-muted-foreground">
            Hriatna Eng Admin Portal
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
